import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../../services/api';
import { useToast } from '../../components/Toast';
import { UserRole } from '@bst-stellar/shared';

export default function EmployeeManagement() {
    const api = useApi();
    const { showToast } = useToast();
    const [employees, setEmployees] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDepartmentModal, setShowDepartmentModal] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState<any>(null);
    const [departments, setDepartments] = useState<any[]>([]);
    const [activeTab, setActiveTab] = useState<'all' | 'pending'>('all');

    useEffect(() => {
        loadEmployees();
        loadDepartments();
    }, []);

    async function loadDepartments() {
        try {
            const result = await api.getDepartments();
            setDepartments(result.departments || []);
        } catch (error: any) {
            console.error('Failed to load departments:', error);
        }
    }

    async function loadEmployees() {
        try {
            setLoading(true);
            const result = await api.getEmployees();
            setEmployees(result.employees || []);
        } catch (error: any) {
            console.error('Failed to load employees:', error);
            showToast(error.message || 'Failed to load employees', 'error');
        } finally {
            setLoading(false);
        }
    }

    async function handleApprove(employee: any) {
        try {
            await api.approveEmployee(employee.id);
            showToast(`${employee.name} has been approved`, 'success');
            loadEmployees();
        } catch (error: any) {
            showToast(error.message || 'Failed to approve employee', 'error');
        }
    }

    async function handleDeactivate(employee: any) {
        if (!confirm(`Are you sure you want to ${employee.status === 'inactive' ? 'activate' : 'deactivate'} ${employee.name}?`)) {
            return;
        }

        try {
            const newStatus = employee.status === 'inactive' ? 'active' : 'inactive';
            await api.updateEmployee(employee.id, { status: newStatus });
            showToast(`${employee.name} has been ${newStatus === 'active' ? 'activated' : 'deactivated'}`, 'success');
            loadEmployees();
        } catch (error: any) {
            showToast(error.message || `Failed to ${employee.status === 'inactive' ? 'activate' : 'deactivate'} employee`, 'error');
        }
    }

    const pendingEmployees = employees.filter(emp => emp.status === 'pending');
    const filteredEmployees = employees
        .filter(emp => activeTab === 'all' || emp.status === 'pending')
        .filter((emp) =>
            emp.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            emp.email.toLowerCase().includes(searchTerm.toLowerCase())
        );

    if (loading) {
        return <div className="text-center py-12 text-text-secondary">Loading employees...</div>;
    }

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-3xl font-bold text-text-primary">Employee Management</h1>
                <button
                    onClick={() => setShowAddModal(true)}
                    className="bg-primary text-white px-4 py-2 rounded hover:opacity-90"
                >
                    + Add Employee
                </button>
            </div>

            {/* Tabs */}
            <div className="flex gap-4 mb-6 border-b border-border">
                <button
                    onClick={() => setActiveTab('all')}
                    className={`pb-3 px-1 font-medium transition ${activeTab === 'all'
                        ? 'text-primary border-b-2 border-primary'
                        : 'text-text-secondary hover:text-text-primary'
                        }`}
                >
                    All Employees ({employees.length})
                </button>
                <button
                    onClick={() => setActiveTab('pending')}
                    className={`pb-3 px-1 font-medium transition flex items-center gap-2 ${activeTab === 'pending'
                        ? 'text-primary border-b-2 border-primary'
                        : 'text-text-secondary hover:text-text-primary'
                        }`}
                >
                    Pending Approval
                    {pendingEmployees.length > 0 && (
                        <span className="bg-yellow-500 text-white text-xs px-2 py-0.5 rounded-full">
                            {pendingEmployees.length}
                        </span>
                    )}
                </button>
            </div>

            {/* Search */}
            <div className="mb-6">
                <input
                    type="text"
                    placeholder="Search employees..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full px-4 py-2 border border-border rounded-lg bg-surface text-text-primary placeholder-text-muted"
                />
            </div>

            {/* Employee Table */}
            <div className="bg-surface rounded-lg shadow overflow-hidden border border-border">
                <table className="w-full">
                    <thead className="bg-background">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                                Name
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                                Email
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                                Position
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                                Role
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                                Status
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-surface divide-y divide-border">
                        {filteredEmployees.map((employee) => (
                            <tr key={employee.id} className="hover:bg-background">
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="font-medium text-text-primary">{employee.name}</div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-text-secondary">
                                    {employee.email}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-text-secondary">
                                    {employee.position}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <RoleBadge role={employee.role} />
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <StatusBadge status={employee.status} />
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                    <Link
                                        to={`/admin/employees/${employee.id}/claims`}
                                        className="text-primary hover:opacity-80 mr-3"
                                    >
                                        All Claims
                                    </Link>
                                    {employee.status === 'pending' && (
                                        <button
                                            onClick={() => handleApprove(employee)}
                                            className="text-green-500 hover:text-green-400 mr-3 font-medium"
                                        >
                                            ✓ Approve
                                        </button>
                                    )}
                                    {employee.status !== 'pending' && (
                                        <button
                                            onClick={() => {
                                                setSelectedEmployee(employee);
                                                setShowDepartmentModal(true);
                                            }}
                                            className="text-green-400 hover:text-green-300 mr-3"
                                        >
                                            Assign Depts
                                        </button>
                                    )}
                                    <button
                                        onClick={() => {
                                            setSelectedEmployee(employee);
                                            setShowEditModal(true);
                                        }}
                                        className="text-primary hover:opacity-80 mr-3"
                                    >
                                        Edit
                                    </button>
                                    <button
                                        onClick={() => handleDeactivate(employee)}
                                        className={`${employee.status === 'inactive' ? 'text-green-400 hover:text-green-300' : 'text-red-400 hover:text-red-300'}`}
                                    >
                                        {employee.status === 'inactive' ? 'Activate' : 'Deactivate'}
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {filteredEmployees.length === 0 && (
                <div className="text-center py-12 text-text-secondary">
                    No employees found
                </div>
            )}

            {/* Add Employee Modal */}
            {showAddModal && (
                <AddEmployeeModal
                    onClose={() => setShowAddModal(false)}
                    onSuccess={() => {
                        setShowAddModal(false);
                        loadEmployees();
                    }}
                />
            )}

            {/* Edit Employee Modal */}
            {showEditModal && selectedEmployee && (
                <EditEmployeeModal
                    employee={selectedEmployee}
                    onClose={() => {
                        setShowEditModal(false);
                        setSelectedEmployee(null);
                    }}
                    onSuccess={() => {
                        setShowEditModal(false);
                        setSelectedEmployee(null);
                        loadEmployees();
                    }}
                />
            )}

            {/* Department Assignment Modal */}
            {showDepartmentModal && selectedEmployee && (
                <DepartmentAssignmentModal
                    employee={selectedEmployee}
                    departments={departments}
                    onClose={() => {
                        setShowDepartmentModal(false);
                        setSelectedEmployee(null);
                    }}
                    onSuccess={() => {
                        setShowDepartmentModal(false);
                        setSelectedEmployee(null);
                        loadEmployees();
                    }}
                />
            )}
        </div>
    );
}

function AddEmployeeModal({ onClose, onSuccess }: { onClose: () => void; onSuccess: () => void }) {
    const api = useApi();
    const { showToast } = useToast();
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        position: '',
        role: UserRole.EMPLOYEE,
        dateOfJoining: '',
        noticePeriodDays: 30,
        startingBalance: '',
    });
    const [submitting, setSubmitting] = useState(false);

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        try {
            setSubmitting(true);
            await api.createEmployee(formData);
            showToast('Employee created successfully!', 'success');
            onSuccess();
        } catch (error: any) {
            console.error('Failed to create employee:', error);
            showToast(error.message || 'Failed to create employee', 'error');
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-surface rounded-lg p-6 w-full max-w-md border border-border">
                <h2 className="text-2xl font-bold mb-4 text-text-primary">Add Employee</h2>
                <form onSubmit={handleSubmit}>
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-text-secondary mb-2">
                            Name *
                        </label>
                        <input
                            type="text"
                            required
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text-primary"
                        />
                    </div>
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-text-secondary mb-2">
                            Email *
                        </label>
                        <input
                            type="email"
                            required
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text-primary"
                        />
                    </div>
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-text-secondary mb-2">
                            Position
                        </label>
                        <input
                            type="text"
                            value={formData.position}
                            onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                            className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text-primary"
                        />
                    </div>
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-text-secondary mb-2">
                            Role
                        </label>
                        <select
                            value={formData.role}
                            onChange={(e) => setFormData({ ...formData, role: e.target.value as UserRole })}
                            className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text-primary"
                        >
                            <option value={UserRole.EMPLOYEE}>Employee</option>
                            <option value={UserRole.DEPT_ADMIN}>Department Admin</option>
                            <option value={UserRole.COMPANY_ADMIN}>Company Admin</option>
                        </select>
                    </div>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                        <div>
                            <label className="block text-sm font-medium text-text-secondary mb-2">
                                Date of Joining
                            </label>
                            <input
                                type="date"
                                value={formData.dateOfJoining}
                                onChange={(e) => setFormData({ ...formData, dateOfJoining: e.target.value })}
                                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text-primary"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-text-secondary mb-2">
                                Notice Period (Days)
                            </label>
                            <input
                                type="number"
                                min="0"
                                value={formData.noticePeriodDays}
                                onChange={(e) => setFormData({ ...formData, noticePeriodDays: parseInt(e.target.value) || 0 })}
                                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text-primary"
                            />
                        </div>
                    </div>
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-text-secondary mb-2">
                            Starting Leave Balance (Optional)
                        </label>
                        <input
                            type="number"
                            min="0"
                            step="0.5"
                            placeholder="Leave empty for auto-calculation"
                            value={formData.startingBalance}
                            onChange={(e) => setFormData({ ...formData, startingBalance: e.target.value })}
                            className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text-primary"
                        />
                        <p className="text-xs text-text-muted mt-1">
                            If provided, this will override the automatic pro-rated calculation.
                        </p>
                    </div>
                    <div className="flex justify-end gap-3">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 border border-border rounded-lg hover:bg-background text-text-secondary"
                            disabled={submitting}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="px-4 py-2 bg-primary text-white rounded-lg hover:opacity-90 disabled:opacity-50"
                            disabled={submitting}
                        >
                            {submitting ? 'Creating...' : 'Create Employee'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function EditEmployeeModal({ employee, onClose, onSuccess }: { employee: any; onClose: () => void; onSuccess: () => void }) {
    const api = useApi();
    const { showToast } = useToast();
    const [formData, setFormData] = useState({
        name: employee.name,
        position: employee.position || '',
        role: employee.role,
        dateOfJoining: employee.dateOfJoining ? new Date(employee.dateOfJoining).toISOString().split('T')[0] : '',
        noticePeriodDays: employee.noticePeriodDays || 30,
    });
    const [submitting, setSubmitting] = useState(false);

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        try {
            setSubmitting(true);
            await api.updateEmployee(employee.id, formData);
            showToast('Employee updated successfully!', 'success');
            onSuccess();
        } catch (error: any) {
            console.error('Failed to update employee:', error);
            showToast(error.message || 'Failed to update employee', 'error');
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-surface rounded-lg p-6 w-full max-w-md border border-border">
                <h2 className="text-2xl font-bold mb-4 text-text-primary">Edit Employee</h2>
                <form onSubmit={handleSubmit}>
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-text-secondary mb-2">
                            Name *
                        </label>
                        <input
                            type="text"
                            required
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text-primary"
                        />
                    </div>
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-text-secondary mb-2">
                            Email
                        </label>
                        <input
                            type="email"
                            disabled
                            value={employee.email}
                            className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text-secondary cursor-not-allowed opacity-70"
                        />
                    </div>
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-text-secondary mb-2">
                            Position
                        </label>
                        <input
                            type="text"
                            value={formData.position}
                            onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                            className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text-primary"
                        />
                    </div>
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-text-secondary mb-2">
                            Role
                        </label>
                        <select
                            value={formData.role}
                            onChange={(e) => setFormData({ ...formData, role: e.target.value as UserRole })}
                            className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text-primary"
                        >
                            <option value={UserRole.EMPLOYEE}>Employee</option>
                            <option value={UserRole.DEPT_ADMIN}>Department Admin</option>
                            <option value={UserRole.COMPANY_ADMIN}>Company Admin</option>
                        </select>
                    </div>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                        <div>
                            <label className="block text-sm font-medium text-text-secondary mb-2">
                                Date of Joining
                            </label>
                            <input
                                type="date"
                                value={formData.dateOfJoining}
                                onChange={(e) => setFormData({ ...formData, dateOfJoining: e.target.value })}
                                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text-primary"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-text-secondary mb-2">
                                Notice Period (Days)
                            </label>
                            <input
                                type="number"
                                min="0"
                                value={formData.noticePeriodDays}
                                onChange={(e) => setFormData({ ...formData, noticePeriodDays: parseInt(e.target.value) || 0 })}
                                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text-primary"
                            />
                        </div>
                    </div>
                    <div className="flex justify-end gap-3">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 border border-border rounded-lg hover:bg-background text-text-secondary"
                            disabled={submitting}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="px-4 py-2 bg-primary text-white rounded-lg hover:opacity-90 disabled:opacity-50"
                            disabled={submitting}
                        >
                            {submitting ? 'Updating...' : 'Update Employee'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function DepartmentAssignmentModal({
    employee,
    departments,
    onClose,
    onSuccess
}: {
    employee: any;
    departments: any[];
    onClose: () => void;
    onSuccess: () => void;
}) {
    const api = useApi();
    const { showToast } = useToast();
    const [selectedDepartmentIds, setSelectedDepartmentIds] = useState<string[]>([]);
    const [submitting, setSubmitting] = useState(false);

    function toggleDepartment(deptId: string) {
        setSelectedDepartmentIds(prev =>
            prev.includes(deptId)
                ? prev.filter(id => id !== deptId)
                : [...prev, deptId]
        );
    }

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        try {
            setSubmitting(true);
            await api.assignDepartments(employee.id, selectedDepartmentIds);
            showToast('Departments assigned successfully!', 'success');
            onSuccess();
        } catch (error: any) {
            console.error('Failed to assign departments:', error);
            showToast(error.message || 'Failed to assign departments', 'error');
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-surface rounded-lg p-6 w-full max-w-md border border-border">
                <h2 className="text-2xl font-bold mb-4 text-text-primary">Assign Departments</h2>
                <p className="text-text-secondary mb-4">
                    Select departments for <strong className="text-text-primary">{employee.name}</strong>
                </p>
                <form onSubmit={handleSubmit}>
                    <div className="mb-4 max-h-64 overflow-y-auto">
                        {departments.map(dept => (
                            <label key={dept.id} className="flex items-center p-2 hover:bg-background rounded cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={selectedDepartmentIds.includes(dept.id)}
                                    onChange={() => toggleDepartment(dept.id)}
                                    className="mr-3"
                                />
                                <span className="text-text-primary">{dept.name}</span>
                            </label>
                        ))}
                        {departments.length === 0 && (
                            <p className="text-text-muted text-sm">No departments available</p>
                        )}
                    </div>
                    <div className="flex justify-end gap-3">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 border border-border rounded-lg hover:bg-background text-text-secondary"
                            disabled={submitting}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="px-4 py-2 bg-primary text-white rounded-lg hover:opacity-90 disabled:opacity-50"
                            disabled={submitting}
                        >
                            {submitting ? 'Assigning...' : 'Assign Departments'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function RoleBadge({ role }: { role: string }) {
    const colors = {
        employee: 'bg-gray-500/20 text-gray-400 border border-gray-500/30',
        dept_admin: 'bg-blue-500/20 text-blue-400 border border-blue-500/30',
        company_admin: 'bg-purple-500/20 text-purple-400 border border-purple-500/30',
    };

    const labels = {
        employee: 'Employee',
        dept_admin: 'Dept Admin',
        company_admin: 'Company Admin',
    };

    return (
        <span
            className={`px-2 py-1 text-xs font-semibold rounded ${colors[role as keyof typeof colors] || 'bg-gray-500/20 text-gray-400'
                }`}
        >
            {labels[role as keyof typeof labels] || role}
        </span>
    );
}

function StatusBadge({ status }: { status: string }) {
    const colors = {
        active: 'bg-green-500/20 text-green-400 border border-green-500/30',
        inactive: 'bg-red-500/20 text-red-400 border border-red-500/30',
    };

    return (
        <span
            className={`px-2 py-1 text-xs font-semibold rounded ${colors[status as keyof typeof colors] || 'bg-gray-500/20 text-gray-400'
                }`}
        >
            {status.toUpperCase()}
        </span>
    );
}

